// Security test suite for verifying implemented security measures
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Shield, AlertTriangle } from 'lucide-react';
import { secureLog, securityValidation, RateLimiter } from '@/lib/security';

interface TestResult {
  name: string;
  status: 'pass' | 'fail' | 'info';
  message: string;
}

export const SecurityTestSuite = ({ onClose }: { onClose: () => void }) => {
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const runSecurityTests = async () => {
    setIsRunning(true);
    const testResults: TestResult[] = [];

    // Test 1: Secure Logging
    try {
      secureLog.info('Security test - logging functionality check');
      testResults.push({
        name: 'Secure Logging System',
        status: 'pass',
        message: 'Secure logging is working correctly'
      });
    } catch (error) {
      testResults.push({
        name: 'Secure Logging System',
        status: 'fail',
        message: 'Secure logging failed to initialize'
      });
    }

    // Test 2: XSS Detection
    const xssTestCases = [
      '<script>alert("xss")</script>',
      'javascript:alert(1)',
      '<iframe src="javascript:alert(1)">',
      'onload="alert(1)"'
    ];

    let xssBlocked = 0;
    xssTestCases.forEach(payload => {
      if (securityValidation.hasXSSPayload(payload)) {
        xssBlocked++;
      }
    });

    testResults.push({
      name: 'XSS Detection',
      status: xssBlocked === xssTestCases.length ? 'pass' : 'fail',
      message: `${xssBlocked}/${xssTestCases.length} XSS payloads detected`
    });

    // Test 3: Email Validation
    const emailTests = [
      { email: 'test@example.com', expected: true },
      { email: 'invalid-email', expected: false },
      { email: 'test@', expected: false },
      { email: '@example.com', expected: false }
    ];

    let emailTestsPassed = 0;
    emailTests.forEach(test => {
      if (securityValidation.isValidEmail(test.email) === test.expected) {
        emailTestsPassed++;
      }
    });

    testResults.push({
      name: 'Email Validation',
      status: emailTestsPassed === emailTests.length ? 'pass' : 'fail',
      message: `${emailTestsPassed}/${emailTests.length} email validation tests passed`
    });

    // Test 4: Rate Limiting
    try {
      const rateLimiter = new RateLimiter(2, 1000); // 2 attempts per second
      const testId = 'security-test';
      
      rateLimiter.recordAttempt(testId);
      rateLimiter.recordAttempt(testId);
      
      const isLimited = rateLimiter.isLimited(testId);
      testResults.push({
        name: 'Rate Limiting',
        status: isLimited ? 'pass' : 'fail',
        message: isLimited ? 'Rate limiting working correctly' : 'Rate limiting not functioning'
      });
    } catch (error) {
      testResults.push({
        name: 'Rate Limiting',
        status: 'fail',
        message: 'Rate limiting system failed to initialize'
      });
    }

    // Test 5: CSP Headers
    const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    const cspContent = cspMeta?.getAttribute('content') || '';
    
    if (cspContent) {
      const hasUnsafeEval = cspContent.includes('unsafe-eval');
      const hasUnsafeInline = cspContent.includes('unsafe-inline');
      
      if (hasUnsafeEval) {
        testResults.push({
          name: 'Content Security Policy',
          status: 'fail',
          message: 'CSP contains unsafe-eval directive'
        });
      } else if (hasUnsafeInline) {
        testResults.push({
          name: 'Content Security Policy',
          status: 'info',
          message: 'CSP contains unsafe-inline (acceptable for styles)'
        });
      } else {
        testResults.push({
          name: 'Content Security Policy',
          status: 'pass',
          message: 'CSP is properly configured'
        });
      }
    } else {
      testResults.push({
        name: 'Content Security Policy',
        status: 'fail',
        message: 'No CSP header found'
      });
    }

    // Test 6: Security Headers
    const securityHeaders = [
      'X-Frame-Options',
      'X-Content-Type-Options',
      'Referrer-Policy'
    ];

    const foundHeaders = securityHeaders.filter(header => 
      document.querySelector(`meta[http-equiv="${header}"]`)
    );

    testResults.push({
      name: 'Security Headers',
      status: foundHeaders.length === securityHeaders.length ? 'pass' : 'info',
      message: `${foundHeaders.length}/${securityHeaders.length} security headers present`
    });

    setResults(testResults);
    setIsRunning(false);
    
    secureLog.info('Security test suite completed', { 
      totalTests: testResults.length,
      passed: testResults.filter(r => r.status === 'pass').length,
      failed: testResults.filter(r => r.status === 'fail').length
    });
  };

  const getIcon = (status: string) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'info':
        return <AlertTriangle className="h-4 w-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getBadgeVariant = (status: string) => {
    switch (status) {
      case 'pass':
        return 'default' as const;
      case 'fail':
        return 'destructive' as const;
      case 'info':
        return 'secondary' as const;
      default:
        return 'outline' as const;
    }
  };

  const passedTests = results.filter(r => r.status === 'pass').length;
  const totalTests = results.length;
  const failedTests = results.filter(r => r.status === 'fail').length;

  return (
    <Card className="fixed top-4 right-4 w-96 max-h-[80vh] overflow-y-auto z-50 shadow-xl border-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Shield className="h-5 w-5" />
          Security Test Suite
          <button 
            onClick={onClose}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            ×
          </button>
        </CardTitle>
        <CardDescription>
          Verify implemented security measures
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {results.length === 0 ? (
          <Alert>
            <AlertDescription>
              Run the security test suite to verify all implemented security measures are working correctly.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <AlertDescription>
              Test Results: {passedTests}/{totalTests} passed
              {failedTests > 0 && ` • ${failedTests} failed`}
            </AlertDescription>
          </Alert>
        )}

        <Button 
          onClick={runSecurityTests} 
          disabled={isRunning}
          className="w-full"
        >
          {isRunning ? 'Running Tests...' : 'Run Security Tests'}
        </Button>

        {results.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-normal text-sm">Test Results:</h4>
            {results.map((result, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getIcon(result.status)}
                    <span className="font-normal text-sm">{result.name}</span>
                  </div>
                  <Badge variant={getBadgeVariant(result.status)} className="text-xs">
                    {result.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground pl-6">
                  {result.message}
                </p>
              </div>
            ))}
          </div>
        )}

        {process.env.NODE_ENV === 'development' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              This test suite only appears in development mode.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};